import { useState } from 'react';
import classNames from 'classnames/bind';
import { Link } from 'react-router-dom';
import loadable from '@utils/loadable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { useSelector } from 'react-redux';

import { TOPICS_SENTIMENT_BREADCRUMBS } from '../../../data/directory/constants';
import { productsAndDevices } from '../../../data/directory/topics/topicsSentimentAnalysis';
import { GOLD } from '../../../data/permissions';

import birdsImage from '../../../assets/homepage/birds.png';

import Breadcrumbs from '../../Breadcrumbs';
import Tooltip from '../../Tooltip';
import withMetatag from '../../../decorators/metadataDecorators/metatagsTitleDecorator';
import TopicClasses from './TopicClasses';
import SectorsType from './SectorsType';
import Locations from './Locations';

import Styles from './styles.module.scss';

const SentimentMiniChart = loadable(() => import('./SentimentMiniChart'));

const cx = classNames.bind(Styles);

const TopicsSentimentAnalysis = () => {
  const [isShowMore, setIsShowMore] = useState(false);

  const toggleExpand = () => {
    setIsShowMore(!isShowMore);
  };
  const isGold = useSelector(({ subscriptions }) => (
    subscriptions.permissions.access_levels.includes(GOLD)
  ));

  return (
    <div className={Styles['sentiment-analysis']}>
      <Breadcrumbs items={TOPICS_SENTIMENT_BREADCRUMBS} />
      <div className={Styles.sentiments_title}>
        <div className={Styles.sentiments_title_text}>
          Sentiment Analysis
        </div>
        <Tooltip
          className={Styles.sentiments_title_tooltip}
          hasBigScreenDesign
        >
          Sentiment shows how positive and negative a story or topic is.&nbsp;
          <a
            href="https://www.cityfalcon.ai/features/sentiment"
            target="_blank"
            rel="noreferrer noopener"
          >
            Learn more
          </a>
        </Tooltip>
      </div>
      <div className={cx('sentiments_desc', { expand: isShowMore })}>
        Word choices and general content impart a specific feeling about a piece of content.
        A story about a plummeting stock market will impart a different idea and feeling than a
        story about a company posting record earnings.
        Sentiment analysis is a tool that takes into account the language used in the
        corresponding content and determines how positive or negative it appears.

        <div className={Styles.sentiments_desc_show} onClick={toggleExpand}>
          <FontAwesomeIcon icon={isShowMore ? faChevronUp : faChevronDown} />
        </div>
      </div>
      <div className={Styles.sentiments_header}>
        <div className={Styles.sentiments_header_left}>
          <div className={Styles.sentiments_header_btn}>
            <a
              href="/features/sentiment"
              className={Styles.button}
            >
              Learn More
            </a>
            <a
              href="/features/sentiment#contact-us"
              className={Styles.button}
            >
              Contact Us
            </a>
          </div>
          <div className={Styles.greeting_available}>
            <div className={Styles.greeting_available_title}>Also Available On:</div>
            <div className={Styles.icons_row}>
              {productsAndDevices.map((item) => (
                <a
                  key={item.id}
                  href={item.path}
                  target={item.newTab ? '_blank' : '_self'}
                  rel="noreferrer"
                  className={Styles.icon_wrapper}
                >
                  <span className={cx('icon', item.class)} />
                  <span className={Styles.name}>{item.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className={Styles.sentiments_header_right}>
          <SentimentMiniChart
            inView
            isMainBox
            isFullLocations
            title="Global Sentiment"
          />
        </div>
      </div>

      <TopicClasses />
      <SectorsType />
      <Locations />

      {!isGold && (
        <div className={Styles['promo-section']}>
          <div className={Styles['promo-section-left']}>
            <h3 className={Styles['promo-section-title']}>
              Access paywalled content, full summaries, and more
            </h3>
            <p className={Styles['promo-section-subtitle']}>
              with our Premium plans.
            </p>
          </div>
          <Link
            to="/pricing"
            className={Styles.button}
          >
            Compare Plans
          </Link>
          <img
            className={Styles['promo-section-icon']}
            src={birdsImage}
            alt="promo icon"
            loading="lazy"
          />
        </div>
      )}
    </div>
  );
};

export default withMetatag(TopicsSentimentAnalysis, 'news/sentiment');
